import { Button, Modal, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { classNames } from 'src/utilities';
import {
  pushEHRCredentials,
  getEHRCredentials,
  deleteEHRCallback,
} from 'src/modules/SuperAdmin/store/actions/ehrcredentials.action';

const DrChronoPage = () => {
  const dispatch = useDispatch();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { ehrCredentialsSuccess } = useSelector(
    (state) => state?.ehrCredentialsState
  );
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleClientIdChange = (e) => {
    setClientId(e.target.value);
  };

  const handleClientSecretChange = (e) => {
    setClientSecret(e.target.value);
  };

  const { ehrCredentialsLoading: _ehrCredentialsLoading } = useSelector(
    (state) => state?.ehrCredentialsState
  );

  const { ehrCredentials } = useSelector((state) => state?.ehrCredentialsState);

  useEffect(() => {
    if (currentPractitioner?.org_uuid) {
      dispatch(getEHRCredentials('drchrono', currentPractitioner?.org_uuid));
    }
  }, [currentPractitioner, dispatch]);

  useEffect(() => {
    if (ehrCredentials) {
      setClientId(ehrCredentials?.client_id);
      setClientSecret(ehrCredentials?.client_secret);
    }
  }, [ehrCredentials]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const org_uuid = currentPractitioner?.org_uuid;
      await dispatch(
        pushEHRCredentials(clientId, clientSecret, org_uuid, 'drchrono')
      );
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ehrCredentialsSuccess) {
      window.location.href = ehrCredentialsSuccess?.auth_url;
    }
  }, [ehrCredentialsSuccess]);

  const handleDeleteCredentials = async () => {
    try {
      setIsLoading(true);
      const org_uuid = currentPractitioner?.org_uuid;
      await dispatch(deleteEHRCallback(null, 'drchrono', org_uuid));

      // Reset all form fields and states to initial values
      setClientId('');
      setClientSecret('');
      setDeleteModalVisible(false);
      setDeleteConfirmText('');

      // Force a refresh of the credentials state
      await dispatch(getEHRCredentials('drchrono', org_uuid));
    } catch (error) {
      console.error('Error deleting credentials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when credentials are cleared
  useEffect(() => {
    if (!ehrCredentials?.client_id) {
      setClientId('');
      setClientSecret('');
    }
  }, [ehrCredentials]);

  return (
    <div className="p-4 sm:p-6">
      <div className="text-lg font-bold font-sans mb-4">
        Setup API Credentials for Dr Chrono
      </div>
      <div className="flex flex-col gap-4">
        <input
          type="text"
          value={clientId}
          onChange={handleClientIdChange}
          placeholder={`Enter Dr Chrono Client ID`}
          readOnly={ehrCredentials?.client_id ? true : false}
          className={classNames(
            'font-sans w-full p-2 border rounded',
            'text-input bg-gray-25 hover:bg-gray-50 transition-colors duration-200',
            'focus:outline-none focus:ring-1 focus:ring-gray-200 focus:border-gray-200',
            ehrCredentials?.client_id ? 'cursor-not-allowed bg-gray-100' : ''
          )}
        />
        <input
          type="password"
          value={clientSecret}
          onChange={handleClientSecretChange}
          placeholder={`Enter Dr Chrono Client Secret`}
          readOnly={ehrCredentials?.client_id ? true : false}
          className={classNames(
            'font-sans w-full p-2 border rounded',
            'text-input bg-gray-25 hover:bg-gray-50 transition-colors duration-200',
            'focus:outline-none focus:ring-1 focus:ring-gray-200 focus:border-gray-200',
            ehrCredentials?.client_id ? 'cursor-not-allowed bg-gray-100' : ''
          )}
        />
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={ehrCredentials?.client_id ? true : false}
          className={classNames(
            'w-full',
            ehrCredentials?.client_id ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          Submit
        </Button>
        {ehrCredentials?.client_id && (
          <Button
            danger
            onClick={() => setDeleteModalVisible(true)}
            className="w-full !bg-red-600 !hover:bg-red-700 !text-white"
            style={{ borderColor: '#dc2626' }}
          >
            Delete Credentials
          </Button>
        )}
        {!ehrCredentials?.client_id && (
          <div className="text-sm text-gray-500 mt-2 italic">
            * After submitting, you will be redirected to Dr Chrono to complete
            the authorization process.
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteConfirmText('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setDeleteModalVisible(false);
              setDeleteConfirmText('');
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={isLoading}
            disabled={deleteConfirmText !== 'delete' || isLoading}
            onClick={handleDeleteCredentials}
          >
            Delete
          </Button>,
        ]}
      >
        <div className="py-4">
          <p className="mb-4">
            This action cannot be undone. To confirm deletion, please type{' '}
            <strong>delete</strong> below:
          </p>
          <Input
            placeholder="Type 'delete' to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            className="w-full"
          />
        </div>
      </Modal>
    </div>
  );
};

export default DrChronoPage;
